import { useState, type FormEvent } from 'react';
import { useAuth } from './AuthProvider';
import { mensajeDeAuth } from './mensajes';
import { useSinRedDeVerdad } from '@/core/red/conectividad';
import { comoEstaElAlmacen } from '@/core/auth/almacenPersistente';
import { estadoDeLaCredencial } from '@/core/auth/sesionOffline';
import { supabase } from '@/core/supabase/client';
import { APP_NAME, urlDeNuevaContrasena } from '@/app/constants';
import { BandaEntorno } from '@/app/BandaEntorno';

/**
 * Entrar, o crear la cuenta.
 *
 * ## Qué se corrigió acá
 *
 * Salió al teléfono y falló en las dos mitades. Al equivocarse la contraseña
 * apareció **«Invalid login credentials»**, en inglés y en rojo: la jerga de la
 * biblioteca de cuentas llegando entera a quien sólo se equivocó al tipear. Y
 * al crear la cuenta, el mensaje de confirmación por correo dejaba a la persona
 * esperando un mail que —sin servidor de correo configurado— no llega nunca.
 *
 * ## Cómo está armado
 *
 * Tres cosas distintas, y se ven distintas: **entrar**, **crear una cuenta** y
 * **recuperar la contraseña**. Antes eran un solo formulario con un enlace que
 * iba rotando entre estados, así que había que leerlo para saber en cuál se
 * estaba.
 *
 * Al crear la cuenta se pide el nombre. No es un trámite: el perfil lo toma de
 * ahí, y sin él la persona queda sin nombre en toda la aplicación —incluida la
 * liquidación, donde figura al lado de su plata—.
 *
 * La contraseña se pide dos veces y se avisa el mínimo ANTES de enviar. El
 * servidor también lo valida, pero enterarse de que era corta después de tocar
 * el botón, y en inglés, es la peor forma de enterarse.
 */
type Modo = 'entrar' | 'registro' | 'olvide';

const MINIMO_CLAVE = 6;

export function LoginPage() {
  // Por qué se está pidiendo entrar. Casi siempre es «porque sí», pero
  // después de una semana sin señal hay algo que explicar.
  const { motivo } = useAuth();
  const sinSenal = useSinRedDeVerdad();

  const [modo, setModoCrudo] = useState<Modo>('entrar');
  const [conEnlace, setConEnlace] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repetida, setRepetida] = useState('');
  const [nombre, setNombre] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Ver lo que se está tipeando.
   *
   * Faltaba, y en un teléfono no es un lujo: sin poder mirarla, una contraseña
   * bien escrita y una mal escrita se ven exactamente igual —ocho puntos—, así
   * que ante un rechazo no hay forma de saber si el problema es la contraseña
   * o la cuenta. Alguien la escribió bien tres veces y siguió sin entrar.
   */
  const [verClave, setVerClave] = useState(false);
  /**
   * Cuando el servidor dice que falta confirmar el correo, se ofrece
   * reenviarlo. Es el único caso en que la persona no puede hacer nada por su
   * cuenta: el mail se perdió, venció, o el enlace no volvió a la aplicación.
   */
  const [faltaConfirmar, setFaltaConfirmar] = useState(false);

  /** Cambiar de pantalla limpia lo dicho: un error viejo sobre otra cosa confunde. */
  const setModo = (m: Modo) => {
    setModoCrudo(m);
    setError(null);
    setInfo(null);
    setRepetida('');
    setFaltaConfirmar(false);
  };

  /** Lo que está mal ANTES de molestar al servidor. */
  const problemaLocal = (): string | null => {
    if (email.trim() === '') return 'Falta el correo.';
    if (modo === 'olvide') return null;
    if (conEnlace && modo === 'entrar') return null;
    if (password.length < MINIMO_CLAVE)
      return `La contraseña tiene que tener al menos ${MINIMO_CLAVE} caracteres.`;
    if (modo === 'registro') {
      if (nombre.trim() === '') return 'Falta tu nombre.';
      if (password !== repetida) return 'Las dos contraseñas no coinciden.';
    }
    return null;
  };

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    const mal = problemaLocal();
    if (mal) {
      setError(mal);
      return;
    }

    setBusy(true);
    try {
      if (modo === 'olvide') {
        // A dónde vuelve el enlace se manda acá y no se deja al «Site URL» del
        // panel, que arrancó valiendo `localhost:3000` y mandaba a todo el mundo
        // a una página que no existe. Desde el teléfono no sirve la dirección
        // actual: adentro del envoltorio de Android no hay una pública.
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: urlDeNuevaContrasena(),
        });
        if (error) throw error;
        setInfo('Si esa dirección tiene cuenta, te enviamos un enlace para cambiar la contraseña.');
      } else if (modo === 'registro') {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          // El perfil toma el nombre de acá. Sin esto, la persona queda sin
          // nombre en toda la aplicación.
          options: { data: { full_name: nombre.trim() } },
        });
        if (error) throw error;
        // Con la confirmación por correo apagada, `signUp` ya deja la sesión
        // abierta y la aplicación sigue sola al asistente de arranque. Con ella
        // encendida no hay sesión, y hay que decirlo con todas las letras: si
        // no, la pantalla queda igual y parece que no pasó nada.
        if (!data.session) {
          setInfo(
            'Te enviamos un correo a ' +
              email.trim() +
              ' para confirmar la dirección. Abrí el enlace y volvé a entrar. ' +
              'Si no llega en unos minutos, revisá el correo no deseado.',
          );
        }
      } else if (conEnlace) {
        const { error } = await supabase.auth.signInWithOtp({ email: email.trim() });
        if (error) throw error;
        setInfo('Te enviamos un enlace de acceso por correo.');
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) throw error;
      }
    } catch (err) {
      const crudo = (err as { message?: string })?.message ?? '';
      setFaltaConfirmar(/email not confirmed|not confirmed/i.test(crudo));
      setError(mensajeDeAuth(err));
    } finally {
      setBusy(false);
    }
  };

  /** Volver a mandar el correo de confirmación. */
  const reenviarConfirmacion = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: email.trim() });
      if (error) throw error;
      setInfo(`Te reenviamos el correo a ${email.trim()}. Revisá también el correo no deseado.`);
      setFaltaConfirmar(false);
    } catch (err) {
      setError(mensajeDeAuth(err));
    } finally {
      setBusy(false);
    }
  };

  const titulo =
    modo === 'registro'
      ? 'Creá tu cuenta y armamos tu veterinaria en cuatro pasos'
      : modo === 'olvide'
        ? 'Te enviamos un enlace para poner una contraseña nueva'
        : 'Ingresá para continuar';

  const boton =
    modo === 'registro'
      ? 'Crear mi cuenta'
      : modo === 'olvide'
        ? 'Enviarme el enlace'
        : conEnlace
          ? 'Enviar enlace'
          : 'Ingresar';

  return (
    // Columna, no fila. La franja de entorno era un hermano más dentro de un
    // `flex` centrado, así que se acomodaba AL LADO del formulario en vez de
    // cruzar el ancho: quedaba una etiqueta suelta a media altura, pisando el
    // campo de correo.
    <main className="flex min-h-full flex-col">
      <BandaEntorno />
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center p-6">
        {/* El logo ES el nombre: lleva la marca escrita adentro. Repetirlo
            debajo en texto sería decir dos veces lo mismo. Queda como texto
            alternativo, que es lo que leen los lectores de pantalla. */}
        <div className="mx-auto mb-4 w-full max-w-[17rem] rounded-2xl border border-[var(--linea)] bg-[var(--superficie)] p-4">
          <img
            src="/marca/logo-boina-finance.png"
            alt={APP_NAME}
            width={640}
            height={269}
            className="block w-full"
            data-testid="logo-marca"
          />
        </div>

        {/* Cuando se llega acá por vencimiento de la gracia sin conexión, se
            dice. «Ingresá para continuar» a secas, después de una semana
            trabajando sin señal, se lee como un error de la aplicación y manda
            a alguien a probar contraseñas en el medio del campo. */}
        {/* Este teléfono nunca entró, y encima no hay señal. Es el callejón
            sin salida: el formulario de abajo no puede servir, porque
            verificar una contraseña es preguntarle al servidor.

            Se dice sólo cuando NO hay señal. En un teléfono nuevo con señal,
            «no entraste todavía» es la situación normal y no hay nada que
            explicar: se entra y listo. */}
        {motivo === 'sin-sesion-previa' && sinSenal ? (
          <p
            className="mb-6 rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-center text-sm text-[var(--aviso)]"
            data-testid="sin-sesion-previa"
          >
            En este teléfono todavía no entraste nunca, y ahora no hay señal. La primera vez hace
            falta conexión; después vas a poder trabajar sin ella.
          </p>
        ) : motivo === 'gracia-vencida' ? (
          <p
            className="mb-6 rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-3 py-2 text-center text-sm text-[var(--aviso)]"
            data-testid="gracia-vencida"
          >
            Pasó una semana sin conectarte. Entrá una vez con señal para poder seguir usando la
            aplicación sin ella.
          </p>
        ) : (
          <p
            className="mb-6 text-center text-sm text-[var(--tinta-suave)]"
            data-testid="login-titulo"
          >
            {titulo}
          </p>
        )}

        {/* Dos solapas, no un enlace que rota. Entrar y crear una cuenta son
            dos cosas distintas y tienen que verse las dos a la vez: con un solo
            enlace había que leerlo para saber en cuál se estaba parado. */}
        {modo !== 'olvide' && (
          <div
            className="mb-4 flex rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-1"
            role="tablist"
            data-testid="login-solapas"
          >
            {(
              [
                ['entrar', 'Iniciar sesión'],
                ['registro', 'Crear cuenta'],
              ] as const
            ).map(([m, rotulo]) => (
              <button
                key={m}
                type="button"
                role="tab"
                aria-selected={modo === m}
                onClick={() => setModo(m)}
                className={`min-h-11 flex-1 rounded-[calc(var(--radius-control)-2px)] px-3 text-sm font-medium ${
                  modo === m
                    ? 'bg-[var(--accion)] text-[var(--sobre-accion)]'
                    : 'text-[var(--tinta-suave)]'
                }`}
                data-testid={`solapa-${m}`}
              >
                {rotulo}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => void enviar(e)}
          className="flex flex-col gap-4"
          data-testid="login-form"
        >
          {modo === 'registro' && (
            <label className="flex flex-col gap-1 text-sm">
              Tu nombre
              <input
                type="text"
                autoComplete="name"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Manuel Dieguez"
                className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
                data-testid="registro-nombre"
              />
              <span className="text-xs text-[var(--tinta-suave)]">
                Con este nombre vas a figurar en los repartos y las liquidaciones.
              </span>
            </label>
          )}

          <label className="flex flex-col gap-1 text-sm">
            Correo
            <input
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              autoCapitalize="none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
              data-testid="login-email"
            />
          </label>

          {modo !== 'olvide' && !(modo === 'entrar' && conEnlace) && (
            <div className="flex flex-col gap-1 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <label htmlFor="campo-clave">Contraseña</label>
                {modo === 'entrar' && (
                  <button
                    type="button"
                    onClick={() => setModo('olvide')}
                    className="text-xs underline opacity-70"
                    data-testid="ir-olvide"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>

              {/* El ojo. Sin él, ocho puntos se ven igual estén bien o mal
                  escritos, y ante un rechazo no hay forma de saber si el
                  problema es lo que se tipeó o la cuenta. */}
              <div className="relative">
                <input
                  id="campo-clave"
                  type={verClave ? 'text' : 'password'}
                  autoComplete={modo === 'registro' ? 'new-password' : 'current-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="min-h-11 w-full rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] pl-3 pr-12"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setVerClave(!verClave)}
                  aria-label={verClave ? 'Ocultar la contraseña' : 'Ver la contraseña'}
                  aria-pressed={verClave}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-[var(--tinta-suave)]"
                  data-testid="ver-clave"
                >
                  {verClave ? <IconoOjoTachado /> : <IconoOjo />}
                </button>
              </div>

              {modo === 'registro' && (
                <span className="text-xs text-[var(--tinta-suave)]">
                  Al menos {MINIMO_CLAVE} caracteres.
                </span>
              )}
            </div>
          )}

          {modo === 'registro' && (
            <label className="flex flex-col gap-1 text-sm">
              Repetí la contraseña
              <input
                type="password"
                autoComplete="new-password"
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
                data-testid="registro-repetida"
              />
            </label>
          )}

          {error && (
            <p role="alert" className="text-sm text-[var(--peligro)]" data-testid="login-error">
              {error}
            </p>
          )}
          {/* El único caso en que la persona no puede hacer nada por su
              cuenta: el correo se perdió, venció, o el enlace no volvió a la
              aplicación. */}
          {faltaConfirmar && (
            <button
              type="button"
              onClick={() => void reenviarConfirmacion()}
              disabled={busy}
              className="min-h-11 self-start text-sm font-medium text-[var(--accion)] underline disabled:opacity-50"
              data-testid="reenviar-confirmacion"
            >
              Reenviarme el correo de confirmación
            </button>
          )}

          {info && (
            <p className="text-sm text-[var(--exito)]" data-testid="login-info">
              {info}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 font-medium text-[var(--sobre-accion)] disabled:opacity-50"
            data-testid="login-submit"
          >
            {busy ? 'Un momento…' : boton}
          </button>

          {/* Las salidas, cada una por su lado y con su nombre. Antes era un
              único enlace que iba rotando entre estados: había que leerlo para
              saber en cuál se estaba parado. */}
          <div className="flex flex-col items-center gap-1 border-t border-[var(--linea)] pt-3">
            {modo === 'entrar' && (
              <button
                type="button"
                onClick={() => {
                  setConEnlace(!conEnlace);
                  setError(null);
                  setInfo(null);
                }}
                className="min-h-11 text-sm underline opacity-60"
                data-testid="cambiar-magic"
              >
                {conEnlace ? 'Prefiero usar contraseña' : 'Prefiero un enlace por correo'}
              </button>
            )}

            {modo === 'olvide' && (
              <button
                type="button"
                onClick={() => setModo('entrar')}
                className="min-h-11 text-sm underline opacity-80"
                data-testid="ir-entrar"
              >
                Volver a iniciar sesión
              </button>
            )}
          </div>
        </form>

        {/* La versión, discreta y siempre.
            No es un adorno: sin esto, dos días de ida y vuelta sobre un fallo
            del arranque sin señal, y ni quien lo reportaba ni quien lo
            arreglaba podían saber si el teléfono tenía el arreglo puesto. Acá
            porque es la pantalla que se fotografía cuando algo no anda. */}
        {/* La versión y el estado de la memoria del teléfono.
            Ninguno es un adorno. Sin la versión, tres APK entregadas sin que
            ninguno de los dos supiera cuál estaba instalada. Sin lo de la
            memoria, cuatro versiones buscando del lado del login un problema
            que estaba en que el teléfono no guardaba nada entre aperturas: un
            almacén que no persiste no tira ningún error, se escribe bien y al
            volver a abrir no hay nada. */}
        <p className="mt-6 text-center text-xs text-[var(--tinta-tenue)]" data-testid="version">
          v{__VERSION__} · {comoEstaElAlmacen()} · {estadoDeLaCredencial()}
        </p>
      </div>
    </main>
  );
}

/** Los dos estados del ojo. Trazo, del tamaño del resto de la aplicación. */
function IconoOjo() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6z" />
      <circle cx="12" cy="12" r="2.8" />
    </svg>
  );
}

function IconoOjoTachado() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-6 10-6c1.6 0 3 .37 4.24.95M22 12s-3.6 6-10 6c-1.6 0-3-.37-4.24-.95" />
      <path d="M4 4l16 16" />
    </svg>
  );
}
